import {
  Button,
  Link,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
} from "@chakra-ui/react";
import { useMemo } from "react";
import { RiAddLine } from "react-icons/ri";
import { SearchableMenuPopover } from "src/components/Popovers/SearchableMenuPopover";
import {
  GENERAL_SEAT_TYPE_LABEL,
  GENERAL_SEAT_TYPE_OPTION_KEY,
  getAvailableSeatTypeOptions,
  groupOfferingOptionsByCategory,
  hasGeneralSeatType,
  type OfferingOption,
  type SchoolOffering,
} from "../offerings";

type SeatTypeOptionItem = {
  key: string;
  label: string;
};

type Props = {
  options: OfferingOption[];
  offering: SchoolOffering;
  onAddSeatType: (optionId: string | null) => void;
  onGoToOfferingCategories: () => void;
};

export const AddSeatTypeButton: React.FC<Props> = ({
  options,
  offering,
  onAddSeatType,
  onGoToOfferingCategories,
}) => {
  const availableOptions = useMemo(
    () =>
      getAvailableSeatTypeOptions({
        options,
        offeringOptionIds: offering.options.map((option) => option.id),
        existingSeatTypeOptionIds: offering.seatTypes.flatMap((seatType) =>
          seatType.option ? [seatType.option.id] : []
        ),
      }),
    [options, offering]
  );
  const canAddGeneral = !hasGeneralSeatType(offering.seatTypes);

  const button = (
    <Button
      size="xs"
      variant="outline"
      colorScheme="gray"
      leftIcon={<RiAddLine />}
      width="fit-content"
      borderStyle="dashed"
      borderColor="gray.300"
    >
      Add seat type
    </Button>
  );

  if (availableOptions.length === 0 && !canAddGeneral) {
    return (
      <Popover placement="bottom-start" gutter={0} isLazy>
        <PopoverTrigger>{button}</PopoverTrigger>
        <PopoverContent p={4}>
          <Text fontSize="sm">
            There are no more options configured for assigning seat types in
            this offering.{" "}
            <Link
              as="button"
              type="button"
              color="primary.500"
              onClick={onGoToOfferingCategories}
            >
              Create it first
            </Link>{" "}
            and then come back to select it.
          </Text>
        </PopoverContent>
      </Popover>
    );
  }

  const optionGroups = [
    ...(canAddGeneral
      ? [
          {
            group: null,
            items: [
              {
                key: GENERAL_SEAT_TYPE_OPTION_KEY,
                label: GENERAL_SEAT_TYPE_LABEL,
              },
            ],
          },
        ]
      : []),
    ...groupOfferingOptionsByCategory(availableOptions),
  ];

  return (
    <SearchableMenuPopover<SeatTypeOptionItem>
      items={optionGroups}
      popoverTrigger={button}
      searchPlaceholder="Search for options"
      popoverProps={{ gutter: 0 }}
      onItemSelected={(option) => {
        onAddSeatType(
          option.key === GENERAL_SEAT_TYPE_OPTION_KEY ? null : option.key
        );
      }}
    />
  );
};
